import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import { FaXTwitter, FaInstagram, FaLinkedinIn, FaTiktok, FaPinterestP, FaFacebookF, FaYoutube } from 'react-icons/fa6';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const { FiArrowRight, FiCheck, FiEdit3, FiZap, FiCalendar, FiTarget, FiImage, FiEye, FiShare2, FiTrendingUp } = FiIcons;

const NETWORKS = [
  { icon: FaXTwitter, label: 'X', color: 'bg-black' },
  { icon: FaInstagram, label: 'Instagram', color: 'bg-gradient-to-br from-fuchsia-500 to-amber-500' },
  { icon: FaLinkedinIn, label: 'LinkedIn', color: 'bg-[#0a66c2]' },
  { icon: FaTiktok, label: 'TikTok', color: 'bg-black' },
  { icon: FaPinterestP, label: 'Pinterest', color: 'bg-[#e60023]' },
  { icon: FaFacebookF, label: 'Facebook', color: 'bg-[#1877f2]' },
  { icon: FaYoutube, label: 'YouTube', color: 'bg-[#ff0000]' },
];

const STEPS = [
  { icon: FiEdit3, title: 'Finish your book', desc: 'Write and polish your manuscript in ManuscriptSmith as usual. Nothing extra to set up.' },
  { icon: FiTarget, title: 'We learn your voice & story', desc: 'Your Author Voice DNA and your book’s themes become the brief — so every post sounds like you, not a bot.' },
  { icon: FiZap, title: 'AI drafts on-brand posts', desc: 'We research what’s resonating in your genre right now and draft a batch of posts — captions, hooks, and branded visuals.' },
  { icon: FiCalendar, title: 'You approve — we queue it', desc: 'Review the queue, tweak anything, and approve the drafts. Scheduling and auto-publishing across connected networks is rolling out soon.' },
];

const FEATURES = [
  { icon: FiEdit3, title: 'Written in your author voice', desc: 'Posts are grounded in your Voice DNA and your actual book — not generic marketing filler.' },
  { icon: FiShare2, title: 'Every network, one place', desc: 'X, Instagram, LinkedIn, TikTok, Pinterest, Facebook & YouTube — connect once, post everywhere.' },
  { icon: FiCalendar, title: 'Calendar & autopilot', desc: 'An always-full content calendar — generate a batch, review it, and approve what goes out. Auto-scheduling coming soon.' },
  { icon: FiTrendingUp, title: 'Research-backed angles', desc: 'Each post is informed by what’s trending in your genre, so you ride the conversation instead of guessing.' },
  { icon: FiImage, title: 'Branded quote cards & visuals', desc: 'On-brand images, quote cards, and short clips generated from your book — in your colors and fonts.' },
  { icon: FiEye, title: 'You review before anything posts', desc: 'Nothing goes live without your nod. Approve, edit, or skip — every draft waits for you.' },
];

const SocialMarketingPage = () => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Book Marketing on Social Media - ManuscriptSmith',
    description: 'We don’t just publish your book — we market it. Turn your finished manuscript into a steady stream of on-brand social posts in your author voice, scheduled and auto-posted across every network.',
    url: 'https://manuscriptsmith.com/book-marketing',
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Book Marketing on Social Media - Get Your Book Read | ManuscriptSmith"
        description="Writing the book is half the battle. ManuscriptSmith doesn’t just publish — we market it: on-brand social posts in your author voice, researched, scheduled, and auto-posted across X, Instagram, LinkedIn, TikTok & more."
        keywords="book marketing, market my book on social media, author social media, promote a self-published book, book launch marketing, auto post book promotion, indie author marketing"
        canonicalUrl="https://manuscriptsmith.com/book-marketing"
        structuredData={structuredData}
      />

      <main>
        {/* Hero */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 text-center">
          <motion.div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 border border-primary-100 text-primary-700 text-xs font-semibold mb-6"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          >
            <SafeIcon icon={FiShare2} className="w-3.5 h-3.5" /> Author Amplification
          </motion.div>

          <motion.h1
            className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          >
            You wrote the book.{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">Now let’s get it read.</span>
          </motion.h1>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            We don’t just <em>publish</em> your book — we <strong>market</strong> it. ManuscriptSmith turns your finished
            manuscript into a steady stream of on-brand social posts, written in your author voice — ready to review and
            publish across every network.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-bold text-lg rounded-lg hover:from-primary-600 hover:to-secondary-600 transition-all shadow-lg">
              Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
            </Link>
            <Link to="/how-it-works" className="inline-flex items-center justify-center px-8 py-4 bg-white border border-parchment-200 text-gray-700 font-semibold text-lg rounded-lg hover:bg-parchment-50 transition-all">
              See how it works
            </Link>
          </div>

          {/* Animated post-cards visual */}
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto text-left">
            {[
              { net: FaXTwitter, ring: 'bg-black', cap: '“The scene I almost cut became the heart of the book. Here’s why I kept it →”' },
              { net: FaInstagram, ring: 'bg-gradient-to-br from-fuchsia-500 to-amber-500', cap: 'Quote card: “We don’t inherit silence — we choose it.” — Chapter 7' },
              { net: FaLinkedinIn, ring: 'bg-[#0a66c2]', cap: 'What 3 years of writing taught me about finishing what you start.' },
            ].map((c, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl border border-parchment-200 shadow-sm p-4"
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className={`w-8 h-8 rounded-full ${c.ring} flex items-center justify-center text-white`}>
                    <SafeIcon icon={c.net} className="w-4 h-4" />
                  </span>
                  <div className="text-xs">
                    <div className="font-semibold text-gray-900">Your author handle</div>
                    <div className="text-gray-400">drafted in your voice</div>
                  </div>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{c.cap}</p>
                <div className="mt-3 flex items-center gap-1.5 text-[11px] font-semibold text-primary-600">
                  <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" /> Ready to schedule
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Problem band */}
        <section className="bg-gray-900 py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-3">Publishing isn’t the finish line</h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-10">
              Most indie books launch into silence — not because they’re bad, but because no one hears about them.
              Marketing is the part authors dread, so it doesn’t happen. We make it the part you don’t have to think about.
            </p>
            <div className="grid sm:grid-cols-3 gap-6">
              {[
                { stat: '90%', label: 'of self-published books sell fewer than 100 copies — visibility, not quality, is the gap.' },
                { stat: '1 post', label: 'is never enough. Consistency wins, and consistency is exactly what burns authors out.' },
                { stat: '0 hrs', label: 'of marketing work from you — you write, we turn it into a sustained campaign.' },
              ].map((s) => (
                <div key={s.stat} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400 mb-2">{s.stat}</div>
                  <p className="text-sm text-gray-400 leading-relaxed">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">From manuscript to a real campaign — in four steps</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {STEPS.map((s, i) => (
                <motion.div
                  key={i}
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                >
                  <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-5">
                    <SafeIcon icon={s.icon} className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-sm font-bold text-gray-400 mb-1">STEP {String(i + 1).padStart(2, '0')}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{s.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">A marketing team, built into your studio</h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">Everything below runs from inside ManuscriptSmith — no new tool to learn, no agency to hire.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                className="bg-white rounded-2xl border border-parchment-200 p-6 hover:shadow-md transition-shadow"
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.1 }}
              >
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mb-4">
                  <SafeIcon icon={f.icon} className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{f.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Per-platform themes showcase */}
        <section className="bg-white py-16 border-t border-parchment-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-50 border border-secondary-100 text-secondary-700 text-xs font-semibold mb-4">
                <SafeIcon icon={FiImage} className="w-3.5 h-3.5" /> Designed for your book — not templated
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-3">Visuals that look like <em>your</em> book</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                We analyze your brand and book, then generate post designs in your palette, your imagery, and your topics —
                so nothing looks like a stock template. Every theme is yours to tune, and fresh theme packs land automatically.
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { name: 'Minimal', bg: 'bg-parchment-50 border border-parchment-200', text: 'text-gray-900', accent: 'text-primary-600',
                  render: (
                    <>
                      <div className="text-[10px] uppercase tracking-widest text-primary-600 mb-2">Chapter excerpt</div>
                      <div className="font-serif text-base leading-snug text-gray-900">“Some doors only open once you stop knocking.”</div>
                    </>
                  ) },
                { name: 'Bold Quote', bg: 'bg-gray-900', text: 'text-white',
                  render: (
                    <>
                      <div className="text-2xl font-bold leading-tight text-white">We don’t inherit silence.</div>
                      <div className="text-2xl font-bold leading-tight text-transparent bg-clip-text bg-gradient-to-r from-primary-400 to-secondary-400">We choose it.</div>
                    </>
                  ) },
                { name: 'Editorial', bg: 'bg-gradient-to-br from-primary-500 to-secondary-500', text: 'text-white',
                  render: (
                    <>
                      <div className="text-[10px] uppercase tracking-widest opacity-80 mb-2">New release</div>
                      <div className="text-lg font-bold leading-snug">The memoir everyone said was “too honest.”</div>
                    </>
                  ) },
                { name: 'Meme', bg: 'bg-amber-300', text: 'text-gray-900',
                  render: (
                    <>
                      <div className="text-base font-extrabold leading-tight">POV: you finally finished the book</div>
                      <div className="mt-2 text-3xl">📖✨</div>
                    </>
                  ) },
              ].map((t, i) => (
                <motion.div
                  key={t.name}
                  className="rounded-2xl shadow-sm overflow-hidden"
                  initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                >
                  <div className={`aspect-square p-5 flex flex-col justify-center ${t.bg} ${t.text}`}>{t.render}</div>
                  <div className="bg-white border-x border-b border-parchment-200 rounded-b-2xl px-3 py-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700">{t.name}</span>
                    <span className="text-[11px] text-primary-600 font-medium inline-flex items-center gap-1"><SafeIcon icon={FiEdit3} className="w-3 h-3" /> Editable</span>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 text-sm">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 text-primary-700 font-semibold">
                <SafeIcon icon={FiZap} className="w-4 h-4" /> New theme packs added every week — automatically
              </span>
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-parchment-100 text-gray-600">
                <SafeIcon icon={FiEdit3} className="w-4 h-4" /> Fine-tune any post in the visual editor
              </span>
            </div>
          </div>
        </section>

        {/* Networks strip */}
        <section className="bg-parchment-100 border-y border-parchment-200 py-14">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Post everywhere from one place</h2>
            <p className="text-gray-600 mb-8">Connect the accounts you have — add the rest whenever you’re ready.</p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {NETWORKS.map((n) => (
                <div key={n.label} className="flex items-center gap-2 bg-white rounded-full border border-parchment-200 pl-1.5 pr-4 py-1.5 shadow-sm">
                  <span className={`w-8 h-8 rounded-full ${n.color} flex items-center justify-center text-white`}>
                    <SafeIcon icon={n.icon} className="w-4 h-4" />
                  </span>
                  <span className="text-sm font-medium text-gray-700">{n.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Positioning band */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <motion.h2
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          >
            We don’t just publish. <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">We market.</span>
          </motion.h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Other tools stop at “your book is live.” That’s where the hard part begins. ManuscriptSmith carries you
            past the finish line — turning the book you already wrote into an audience that actually finds it.
          </p>
        </section>

        {/* Inclusion note */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
          <div className="bg-parchment-100 border border-parchment-200 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Writing first. Marketing when you’re ready.</h2>
            <ul className="space-y-3">
              {[
                'Your editor stays focused on writing — the marketing engine lives in its own tab, off until you switch it on.',
                'Posts are always drafted from your finished book and Author Voice DNA — never generic, never off-brand.',
                'Nothing publishes without your approval — review the queue and edit anything before it goes live.',
                'Included with the Pro plan; available a la carte on Author.',
              ].map((t) => (
                <li key={t} className="flex items-start">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-primary-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-10 text-center text-white shadow-xl">
            <h2 className="text-3xl font-bold mb-4">Write it. Then let the world hear about it.</h2>
            <p className="text-lg opacity-90 mb-8">Get started — turn on book marketing whenever your story is ready to be read.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-600 font-bold text-lg rounded-lg hover:bg-gray-50 transition-all">
                Get started <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-2" />
              </Link>
              <Link to="/pricing" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 border border-white/40 text-white font-semibold text-lg rounded-lg hover:bg-white/25 transition-all">
                See pricing
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SocialMarketingPage;
